import EmailIcon from '@mui/icons-material/Email';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SendIcon from '@mui/icons-material/Send';
import SmsIcon from '@mui/icons-material/Sms';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import {
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import axios from 'axios';
import { MouseEvent, useCallback, useEffect, useState } from 'react';
import {
  cancelCommunication,
  CommunicationMessage,
  CommunicationRecipient,
  CommunicationStatus,
  CreateCommunicationPayload,
  createCommunication,
  getCommunication,
  listCommunications,
  sendCommunication,
  updateCommunication,
} from 'src/api/communications';
import { listOthRegistrationSubmissions } from 'src/api/othRegistration';
import { MuiModal } from 'src/components/material/MuiModal';

import styles from './OthCommunicationTab.module.scss';
import {
  DEFAULT_REPLY_TO_EMAIL,
  getInitialReplyToEmail,
  getReplyToEmailError,
  normalizeReplyToEmail,
} from './replyToEmail';

type LocalRecipient = CommunicationRecipient & {
  id: string;
  name: string;
};

type ChannelOption = 'sms' | 'email';
type AudienceMode = 'all' | 'selected';
type SendTiming = 'now' | 'later';

type OthCommunicationTabProps = {
  circleId: string;
};

const getChannelIcon = (message: CommunicationMessage) => {
  const hasSms = message.channels.includes('SMS');
  const hasEmail = message.channels.includes('EMAIL');

  if (hasSms && hasEmail) {
    return (
      <>
        <SmsIcon fontSize="small" />
        <EmailIcon fontSize="small" />
      </>
    );
  }

  if (hasEmail) {
    return <EmailIcon fontSize="small" />;
  }

  return <SmsIcon fontSize="small" />;
};

const getStatusClassName = (message: CommunicationMessage) => {
  if (message.status === 'SENT') {
    return message.sentCount === message.recipientCount
      ? styles.statusSentComplete
      : styles.statusSentIncomplete;
  }

  if (message.status === 'SCHEDULED' || message.status === 'SENDING') {
    return styles.statusScheduled;
  }

  if (message.status === 'FAILED' || message.status === 'PARTIALLY_SENT') {
    return styles.statusSentIncomplete;
  }

  return styles.statusDraft;
};

const statusLabels: Record<CommunicationStatus, string> = {
  DRAFT: 'Draft',
  SCHEDULED: 'Scheduled',
  SENDING: 'Sending',
  SENT: 'Sent',
  PARTIALLY_SENT: 'Partially sent',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
};

const getChannelLabel = (message: CommunicationMessage) =>
  message.channels
    .map((channel) => (channel === 'SMS' ? 'SMS' : 'Email'))
    .join(' and ');

const getChannelSelectionLabel = (
  smsEnabled: boolean,
  emailEnabled: boolean
) => {
  if (smsEnabled && emailEnabled) {
    return 'SMS and email';
  }

  return smsEnabled ? 'SMS' : 'Email';
};

const getRecipientContact = (recipient: CommunicationRecipient) =>
  [recipient.email, recipient.phone].filter(Boolean).join(' / ');

const getRecipientName = (recipient: CommunicationRecipient) =>
  recipient.name || recipient.email || recipient.phone || 'Unknown';

const getMessageRecipients = (message: CommunicationMessage) =>
  message.recipients ?? message.audience?.recipients ?? [];

const isEmailAddress = (value: string) => /\S+@\S+\.\S+/.test(value);

const isPhoneNumber = (value: string) => {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 5 && /^[+\d\s-]+$/.test(value);
};

const formatDateTime = (value?: string) => {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  return date.toLocaleString('nb-NO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getSentTimeLabel = (message: CommunicationMessage) => {
  const sentAt = formatDateTime(message.sentAt);
  if (sentAt) {
    return `Sent ${sentAt}`;
  }

  const scheduledAt = formatDateTime(message.scheduledAt || message.sendAt);
  if (scheduledAt) {
    return `Scheduled for ${scheduledAt}`;
  }

  return 'Not sent yet';
};

const getRecipientCountLabel = (message: CommunicationMessage) => {
  const recipients = getMessageRecipients(message);
  const recipientCount = message.recipientCount ?? recipients.length;

  if (typeof message.sentCount === 'number') {
    return `Sent to ${message.sentCount}/${recipientCount}`;
  }

  return `${recipientCount} recipients`;
};

const getBackendErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      { message?: string; error?: string } | undefined;
    if (data?.message || data?.error) {
      return data.message || data.error || 'Something went wrong.';
    }

    if (error.response?.status === 403) {
      return 'You must be a leader or co-leader of this OTH circle to send communications.';
    }

    if (error.response?.status === 409) {
      return 'This communication cannot be changed in its current status.';
    }
  }

  return 'Something went wrong. Please try again.';
};

const formatScheduleDate = (date: string, time: string) => {
  if (!date || !time) {
    return 'Not selected';
  }

  const scheduleDateTime = getScheduleDateTime(date, time);
  if (!scheduleDateTime) {
    return 'Not selected';
  }

  return `${scheduleDateTime.toLocaleDateString('nb-NO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })}, ${time}`;
};

const padDatePart = (value: number) => String(value).padStart(2, '0');

const toDateInputValue = (date: Date) =>
  `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(
    date.getDate()
  )}`;

const toTimeInputValue = (date: Date) =>
  `${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`;

const getScheduleDateTime = (date: string, time: string) => {
  if (!date || !time) {
    return null;
  }

  const scheduleDateTime = new Date(`${date}T${time}`);
  return Number.isNaN(scheduleDateTime.getTime()) ? null : scheduleDateTime;
};

const TWILIO_ALPHA_SENDER_MAX_LENGTH = 11;
// Twilio alphanumeric sender ID: 1–11 chars, A-Z a-z 0-9 space + - _ &, at least one letter.
const TWILIO_ALPHA_SENDER_PATTERN = /^(?=.*[A-Za-z])[A-Za-z0-9 +_\-&]{1,11}$/;

const getSmsSenderError = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  if (trimmed.length > TWILIO_ALPHA_SENDER_MAX_LENGTH) {
    return `Sender can be max ${TWILIO_ALPHA_SENDER_MAX_LENGTH} characters (Twilio).`;
  }
  if (!TWILIO_ALPHA_SENDER_PATTERN.test(trimmed)) {
    return 'Sender must be 1–11 characters, include a letter, and only use A–Z, a–z, 0–9, space, +, -, _ or &.';
  }
  return null;
};

export const OthCommunicationTab = ({ circleId }: OthCommunicationTabProps) => {
  const [messages, setMessages] = useState<CommunicationMessage[]>([]);
  const [registeredRecipients, setRegisteredRecipients] = useState<
    LocalRecipient[]
  >([]);
  const [manualRecipients, setManualRecipients] = useState<LocalRecipient[]>(
    []
  );
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);
  const [recipientsError, setRecipientsError] = useState<string | null>(null);
  const [actionsAnchorEl, setActionsAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [detailsMessage, setDetailsMessage] =
    useState<CommunicationMessage | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [isRecipientsOverlayOpen, setIsRecipientsOverlayOpen] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [enabledChannels, setEnabledChannels] = useState<
    Record<ChannelOption, boolean>
  >({ sms: true, email: false });
  const [messageName, setMessageName] = useState('New OTH message');
  const [messageBody, setMessageBody] = useState(
    'Hi! Here is a short update from OTH. More information will follow in the app.'
  );
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [replyToEmail, setReplyToEmail] = useState(DEFAULT_REPLY_TO_EMAIL);
  const [sender, setSender] = useState('OTH');
  const [audienceMode, setAudienceMode] = useState<AudienceMode>('all');
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<string[]>(
    []
  );
  const [recipientSearch, setRecipientSearch] = useState('');
  const [manualRecipientName, setManualRecipientName] = useState('');
  const [manualRecipientPhone, setManualRecipientPhone] = useState('');
  const [manualRecipientError, setManualRecipientError] = useState<
    string | null
  >(null);
  const [sendTiming, setSendTiming] = useState<SendTiming>('now');
  const [scheduleDate, setScheduleDate] = useState(() =>
    toDateInputValue(new Date(Date.now() + 60 * 60 * 1000))
  );
  const [scheduleTime, setScheduleTime] = useState(() =>
    toTimeInputValue(new Date(Date.now() + 60 * 60 * 1000))
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const loadMessages = useCallback(async () => {
    try {
      setIsLoadingMessages(true);
      setMessagesError(null);
      const result = await listCommunications({
        pageSize: 20,
        tribe_id: circleId,
      });
      setMessages(result.result);
    } catch (error) {
      setMessagesError(getBackendErrorMessage(error));
    } finally {
      setIsLoadingMessages(false);
    }
  }, [circleId]);

  const loadRegisteredRecipients = useCallback(async () => {
    try {
      setIsLoadingRecipients(true);
      setRecipientsError(null);
      const result = await listOthRegistrationSubmissions(circleId);
      const recipients = result.submissions.map((submission) => {
        const name = [submission.first_name, submission.surname]
          .filter(Boolean)
          .join(' ')
          .trim();

        return {
          id: submission.id,
          name:
            name || submission.email || submission.phone_number || 'Unknown',
          phone: submission.phone_number || undefined,
          email: submission.email || undefined,
        };
      });

      setRegisteredRecipients(recipients);
      setSelectedRecipientIds(recipients.map((recipient) => recipient.id));
    } catch {
      setRecipientsError('Could not load registered participants.');
    } finally {
      setIsLoadingRecipients(false);
    }
  }, [circleId]);

  useEffect(() => {
    loadRegisteredRecipients();
  }, [loadRegisteredRecipients]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const isActionsMenuOpen = Boolean(actionsAnchorEl);
  const recipientControlsDisabled = isLoadingRecipients || !!recipientsError;
  const activeMessage = messages.find(
    (message) => message._id === activeMessageId
  );
  const allSelectableRecipients = [
    ...registeredRecipients,
    ...manualRecipients,
  ];
  const minScheduleDateTime = new Date();
  const minScheduleDate = toDateInputValue(minScheduleDateTime);
  const minScheduleTime = toTimeInputValue(minScheduleDateTime);
  const selectedScheduleDateTime = getScheduleDateTime(
    scheduleDate,
    scheduleTime
  );
  const isScheduleValid =
    sendTiming === 'now' ||
    (selectedScheduleDateTime !== null &&
      selectedScheduleDateTime >= minScheduleDateTime);
  const senderError = getSmsSenderError(sender);
  const replyToEmailError = enabledChannels.email
    ? getReplyToEmailError(replyToEmail)
    : null;
  const messageError =
    messageBody.length > 1000
      ? 'SMS message can be max 1000 characters.'
      : null;
  const selectedRecipients =
    audienceMode === 'all'
      ? registeredRecipients
      : allSelectableRecipients.filter((recipient) =>
          selectedRecipientIds.includes(recipient.id)
        );
  const selectedChannelRecipients = selectedRecipients.filter(
    (recipient) =>
      (enabledChannels.sms && Boolean(recipient.phone?.trim())) ||
      (enabledChannels.email && Boolean(recipient.email?.trim()))
  );
  const filteredRecipients = allSelectableRecipients.filter((recipient) => {
    const query = recipientSearch.trim().toLowerCase();
    if (!query) {
      return true;
    }

    return `${recipient.name} ${getRecipientContact(recipient)}`
      .toLowerCase()
      .includes(query);
  });
  const hasValidSmsContent =
    !enabledChannels.sms ||
    (messageBody.trim() && !senderError && !messageError);
  const hasValidEmailContent =
    !enabledChannels.email ||
    (emailSubject.trim() && emailBody.trim() && !replyToEmailError);
  const canContinueFromMessage =
    (enabledChannels.sms || enabledChannels.email) &&
    hasValidSmsContent &&
    hasValidEmailContent;
  const canCreateMessage =
    canContinueFromMessage &&
    (audienceMode === 'all' || selectedChannelRecipients.length > 0) &&
    isScheduleValid;

  const handleCreateMessage = () => {
    setEditingMessageId(null);
    setCreateStep(1);
    setSubmitError(null);
    setEnabledChannels({ sms: true, email: false });
    setMessageName('New OTH message');
    setMessageBody(
      'Hi! Here is a short update from OTH. More information will follow in the app.'
    );
    setEmailSubject('');
    setEmailBody('');
    setReplyToEmail(DEFAULT_REPLY_TO_EMAIL);
    setSender('OTH');
    setAudienceMode('all');
    setManualRecipients([]);
    setSelectedRecipientIds(
      registeredRecipients.map((recipient) => recipient.id)
    );
    setSendTiming('now');
    setIsCreateModalOpen(true);
  };

  const handleEditDraft = async () => {
    const message = activeMessage;
    handleActionsClose();

    if (!message || message.status !== 'DRAFT') {
      return;
    }

    try {
      setMessagesError(null);
      const draft = await getCommunication(message._id);
      const draftRecipients = getMessageRecipients(draft).map(
        (recipient, index): LocalRecipient => ({
          ...recipient,
          id: `draft-${draft._id}-${index}`,
          name: getRecipientName(recipient),
        })
      );
      const scheduledDateTime = draft.sendAt ? new Date(draft.sendAt) : null;
      const hasValidSchedule =
        scheduledDateTime && !Number.isNaN(scheduledDateTime.getTime());

      setEditingMessageId(draft._id);
      setCreateStep(1);
      setSubmitError(null);
      setEnabledChannels({
        sms: draft.channels.includes('SMS'),
        email: draft.channels.includes('EMAIL'),
      });
      setMessageName(draft.name ?? '');
      setMessageBody(draft.sms?.message ?? '');
      setSender(draft.sms?.sender ?? '');
      setEmailSubject(draft.email?.subject ?? '');
      setEmailBody(draft.email?.body ?? '');
      setReplyToEmail(getInitialReplyToEmail(draft.email?.replyToEmail));
      setAudienceMode(draft.audience?.type === 'EXPLICIT' ? 'selected' : 'all');
      setManualRecipients(draftRecipients);
      setSelectedRecipientIds(draftRecipients.map((recipient) => recipient.id));
      setSendTiming(hasValidSchedule ? 'later' : 'now');
      if (hasValidSchedule && scheduledDateTime) {
        setScheduleDate(toDateInputValue(scheduledDateTime));
        setScheduleTime(toTimeInputValue(scheduledDateTime));
      }
      setIsCreateModalOpen(true);
    } catch (error) {
      setMessagesError(getBackendErrorMessage(error));
    }
  };

  const handleOpenDetails = async (message: CommunicationMessage) => {
    setDetailsMessage(message);
    setDetailsError(null);
    setIsRecipientsOverlayOpen(false);
    setIsDetailsModalOpen(true);

    try {
      setIsLoadingDetails(true);
      const fullMessage = await getCommunication(message._id);
      setDetailsMessage(fullMessage);
    } catch (error) {
      setDetailsError(getBackendErrorMessage(error));
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleCloseDetails = (open: boolean) => {
    setIsDetailsModalOpen(open);
    if (!open) {
      setIsRecipientsOverlayOpen(false);
      setDetailsError(null);
    }
  };

  const handleToggleChannel = (channel: ChannelOption) => {
    setEnabledChannels((prev) => ({ ...prev, [channel]: !prev[channel] }));
  };

  const handleToggleRecipient = (recipientId: string) => {
    if (audienceMode === 'all') {
      setAudienceMode('selected');
      setSelectedRecipientIds(
        allSelectableRecipients
          .map((recipient) => recipient.id)
          .filter((selectedRecipientId) => selectedRecipientId !== recipientId)
      );
      return;
    }

    setSelectedRecipientIds((prev) =>
      prev.includes(recipientId)
        ? prev.filter(
            (selectedRecipientId) => selectedRecipientId !== recipientId
          )
        : [...prev, recipientId]
    );
  };

  const handleScheduleDateChange = (date: string) => {
    setScheduleDate(date);
    if (date === minScheduleDate && scheduleTime < minScheduleTime) {
      setScheduleTime(minScheduleTime);
    }
  };

  const handleScheduleTimeChange = (time: string) => {
    if (scheduleDate === minScheduleDate && time < minScheduleTime) {
      setScheduleTime(minScheduleTime);
      return;
    }

    setScheduleTime(time);
  };

  const handleSelectVisibleRecipients = () => {
    setAudienceMode('selected');
    setSelectedRecipientIds((prev) =>
      Array.from(
        new Set([
          ...prev,
          ...filteredRecipients.map((recipient) => recipient.id),
        ])
      )
    );
  };

  const handleClearSelectedRecipients = () => {
    setAudienceMode('selected');
    const visibleContacts = new Set(
      filteredRecipients.map((recipient) => recipient.id)
    );
    setSelectedRecipientIds((prev) =>
      prev.filter((recipientId) => !visibleContacts.has(recipientId))
    );
  };

  const handleAddManualRecipient = () => {
    const contact = manualRecipientPhone.trim();
    const isEmail = isEmailAddress(contact);
    const isPhone = isPhoneNumber(contact);

    if (!contact || (!isEmail && !isPhone)) {
      setManualRecipientError('Enter a valid phone number or email address.');
      return;
    }

    const recipient: LocalRecipient = {
      id: `manual-${Date.now()}`,
      name: manualRecipientName.trim() || contact,
      phone: isPhone ? contact : undefined,
      email: isEmail ? contact : undefined,
    };

    setManualRecipients((prev) => [...prev, recipient]);
    setSelectedRecipientIds((prev) => [...prev, recipient.id]);
    setManualRecipientName('');
    setManualRecipientPhone('');
    setManualRecipientError(null);
    setAudienceMode('selected');
  };

  const handleSaveCreatedMessage = async () => {
    if (!canCreateMessage) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const sendAt =
        sendTiming === 'later' && selectedScheduleDateTime
          ? selectedScheduleDateTime.toISOString()
          : undefined;
      const payload: CreateCommunicationPayload = {
        name: messageName.trim() || undefined,
        channels: [
          ...(enabledChannels.sms ? ['SMS' as const] : []),
          ...(enabledChannels.email ? ['EMAIL' as const] : []),
        ],
        sms: enabledChannels.sms
          ? {
              message: messageBody.trim(),
              sender: sender.trim() || undefined,
            }
          : undefined,
        email: enabledChannels.email
          ? {
              subject: emailSubject.trim(),
              body: emailBody.trim(),
              replyToEmail: normalizeReplyToEmail(replyToEmail),
            }
          : undefined,
        audience:
          audienceMode === 'all'
            ? {
                type: 'OTH_REGISTRATIONS',
                tribe_id: circleId,
              }
            : {
                type: 'EXPLICIT',
                tribe_id: circleId,
                recipients: selectedChannelRecipients.map((recipient) => ({
                  name: recipient.name,
                  phone: recipient.phone,
                  email: recipient.email,
                })),
              },
        sendAt,
      };
      const created = editingMessageId
        ? await updateCommunication(editingMessageId, payload)
        : await createCommunication(payload);
      const saved =
        sendTiming === 'now' ? await sendCommunication(created._id) : created;

      setMessages((prev) => [
        saved,
        ...prev.filter((message) => message._id !== saved._id),
      ]);
      setIsCreateModalOpen(false);
      setEditingMessageId(null);
    } catch (error) {
      setSubmitError(getBackendErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActionsOpen = (
    event: MouseEvent<HTMLButtonElement>,
    messageId: string
  ) => {
    setActionsAnchorEl(event.currentTarget);
    setActiveMessageId(messageId);
  };

  const handleActionsClose = () => {
    setActionsAnchorEl(null);
    setActiveMessageId(null);
  };

  const handleStopMessage = async () => {
    if (!activeMessageId || activeMessage?.status !== 'SCHEDULED') {
      return;
    }

    try {
      const cancelled = await cancelCommunication(activeMessageId);
      setMessages((prev) =>
        prev.map((message) =>
          message._id === cancelled._id ? cancelled : message
        )
      );
    } catch (error) {
      setMessagesError(getBackendErrorMessage(error));
    } finally {
      handleActionsClose();
    }
  };

  return (
    <section className={styles.container}>
      <div className={styles.actions}>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={handleCreateMessage}
        >
          Create message
        </Button>
      </div>

      <div>
        {isLoadingMessages ? (
          <p className={styles.empty}>Loading communications...</p>
        ) : messagesError ? (
          <p className={styles.error}>{messagesError}</p>
        ) : messages.length === 0 ? (
          <p className={styles.empty}>No messages created yet.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Audience</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((message) => {
                  const recipients = getMessageRecipients(message);
                  const recipientCount =
                    message.recipientCount ?? recipients.length;
                  const statusDate = formatDateTime(
                    message.sentAt || message.scheduledAt || message.sendAt
                  );

                  return (
                    <tr
                      key={message._id}
                      className={styles.clickableRow}
                      onClick={() => handleOpenDetails(message)}
                    >
                      <td>
                        <div className={styles.messageCell}>
                          <strong className={styles.messageName}>
                            {message.name || 'Untitled communication'}
                          </strong>
                          <span className={styles.messageChannel}>
                            <span className={styles.channelIcons}>
                              {getChannelIcon(message)}
                            </span>
                            {getChannelLabel(message)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className={styles.statusCell}>
                          <span
                            className={`${styles.statusText} ${getStatusClassName(
                              message
                            )}`}
                          >
                            {statusLabels[message.status] ?? message.status}
                          </span>
                          {statusDate && (
                            <span className={styles.statusMeta}>
                              {statusDate}
                            </span>
                          )}
                          {message.error && (
                            <span className={styles.error}>
                              {message.error}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className={styles.audienceCell}>
                          <span>
                            {message.audience?.type === 'OTH_REGISTRATIONS'
                              ? 'All OTH registrations'
                              : 'Selected recipients'}
                          </span>
                          <span
                            className={styles.recipientsMarker}
                            onClick={(event) => event.stopPropagation()}
                          >
                            <strong>{recipientCount}</strong> recipients
                            {recipients.length > 0 && (
                              <span className={styles.recipientsOverlay}>
                                {recipients.map((recipient, index) => (
                                  <span
                                    key={`${message._id}-${index}`}
                                    className={styles.recipientRow}
                                  >
                                    <strong>
                                      {getRecipientName(recipient)}
                                    </strong>
                                    <span>
                                      {getRecipientContact(recipient)}
                                    </span>
                                  </span>
                                ))}
                              </span>
                            )}
                          </span>
                          {typeof message.sentCount === 'number' &&
                            typeof recipientCount === 'number' && (
                              <span
                                className={`${styles.deliveryMeta} ${
                                  message.sentCount === recipientCount
                                    ? styles.deliveryComplete
                                    : styles.deliveryIncomplete
                                }`}
                              >
                                Sent to {message.sentCount}/{recipientCount}
                              </span>
                            )}
                        </div>
                      </td>
                      <td className={styles.actionsCell}>
                        <IconButton
                          size="small"
                          className={styles.moreButton}
                          aria-label={`More options for ${
                            message.name || 'communication'
                          }`}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleActionsOpen(event, message._id);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <MuiModal
        open={isCreateModalOpen}
        setOpen={setIsCreateModalOpen}
        width={820}
      >
        <div className={styles.createModal}>
          <div className={styles.modalHeader}>
            <div>
              <h2 className={styles.modalTitle}>
                {editingMessageId ? 'Edit draft' : 'Create message'}
              </h2>
              <p className={styles.modalSubtitle}>
                Set up channel, audience, and send time.
              </p>
            </div>
            <div className={styles.stepIndicator}>Step {createStep} of 3</div>
          </div>

          <div className={styles.steps}>
            {['Message', 'Audience', 'Send'].map((step, index) => (
              <div
                key={step}
                className={`${styles.stepPill} ${
                  createStep === index + 1 ? styles.stepPillActive : ''
                }`}
              >
                {index + 1}. {step}
              </div>
            ))}
          </div>

          {createStep === 1 && (
            <div className={styles.stepContent}>
              <h3 className={styles.sectionTitle}>Choose channel</h3>
              <div className={styles.channelCards}>
                <label
                  className={`${styles.optionCard} ${
                    enabledChannels.sms ? styles.optionCardSelected : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={enabledChannels.sms}
                    onChange={() => handleToggleChannel('sms')}
                  />
                  <span>
                    <strong>SMS</strong>
                    <small>Short message to mobile.</small>
                  </span>
                </label>
                <label
                  className={`${styles.optionCard} ${
                    enabledChannels.email ? styles.optionCardSelected : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={enabledChannels.email}
                    onChange={() => handleToggleChannel('email')}
                  />
                  <span>
                    <strong>Email</strong>
                    <small>Message sent to registered email addresses.</small>
                  </span>
                </label>
              </div>

              <label className={styles.fieldGroup}>
                <span>Message name</span>
                <input
                  className={styles.textInput}
                  value={messageName}
                  onChange={(event) => setMessageName(event.target.value)}
                  placeholder="E.g. Welcome to OTH"
                />
              </label>

              {enabledChannels.sms && (
                <>
                  <label className={styles.fieldGroup}>
                    <span>Sender</span>
                    <input
                      className={styles.textInput}
                      maxLength={TWILIO_ALPHA_SENDER_MAX_LENGTH}
                      value={sender}
                      onChange={(event) => setSender(event.target.value)}
                      placeholder="OTH"
                    />
                    <small className={styles.fieldHint}>
                      Optional. Max {TWILIO_ALPHA_SENDER_MAX_LENGTH} characters
                      (Twilio alphanumeric sender ID). Empty uses backend
                      default sender.
                    </small>
                    {senderError && (
                      <small className={styles.fieldError}>{senderError}</small>
                    )}
                  </label>

                  <label className={styles.fieldGroup}>
                    <span>SMS text</span>
                    <textarea
                      className={styles.textArea}
                      value={messageBody}
                      onChange={(event) => setMessageBody(event.target.value)}
                      maxLength={1000}
                      placeholder="Write SMS text here..."
                      rows={6}
                    />
                    <small className={styles.fieldHint}>
                      {messageBody.length}/1000 characters
                    </small>
                    {messageError && (
                      <small className={styles.fieldError}>
                        {messageError}
                      </small>
                    )}
                  </label>
                </>
              )}

              {enabledChannels.email && (
                <>
                  <label className={styles.fieldGroup}>
                    <span>Reply-To</span>
                    <input
                      className={styles.textInput}
                      type="email"
                      value={replyToEmail}
                      onChange={(event) => setReplyToEmail(event.target.value)}
                      onBlur={() =>
                        setReplyToEmail(normalizeReplyToEmail(replyToEmail))
                      }
                      aria-invalid={Boolean(replyToEmailError)}
                      aria-describedby={
                        replyToEmailError ? 'reply-to-email-error' : undefined
                      }
                      placeholder={DEFAULT_REPLY_TO_EMAIL}
                    />
                    <small className={styles.fieldHint}>
                      Must be an @ordtilhandling.no email address.
                    </small>
                    {replyToEmailError && (
                      <small
                        id="reply-to-email-error"
                        className={styles.fieldError}
                      >
                        {replyToEmailError}
                      </small>
                    )}
                  </label>

                  <label className={styles.fieldGroup}>
                    <span>Email subject</span>
                    <input
                      className={styles.textInput}
                      value={emailSubject}
                      onChange={(event) => setEmailSubject(event.target.value)}
                      maxLength={200}
                      placeholder="Write email subject here..."
                    />
                  </label>

                  <label className={styles.fieldGroup}>
                    <span>Email message</span>
                    <textarea
                      className={styles.textArea}
                      value={emailBody}
                      onChange={(event) => setEmailBody(event.target.value)}
                      maxLength={50000}
                      placeholder="Write email text here..."
                      rows={8}
                    />
                    <small className={styles.fieldHint}>
                      {emailBody.length}/50000 characters
                    </small>
                  </label>
                </>
              )}
            </div>
          )}

          {createStep === 2 && (
            <div className={styles.stepContent}>
              <h3 className={styles.sectionTitle}>Choose recipients</h3>
              <div className={styles.audienceOptions}>
                <label
                  className={`${styles.optionCard} ${
                    audienceMode === 'all' ? styles.optionCardSelected : ''
                  }`}
                >
                  <input
                    type="radio"
                    checked={audienceMode === 'all'}
                    onChange={() => setAudienceMode('all')}
                  />
                  <span>
                    <strong>All</strong>
                    <small>Use backend OTH registrations audience.</small>
                  </span>
                </label>
                <label
                  className={`${styles.optionCard} ${
                    audienceMode === 'selected' ? styles.optionCardSelected : ''
                  }`}
                >
                  <input
                    type="radio"
                    checked={audienceMode === 'selected'}
                    onChange={() => setAudienceMode('selected')}
                  />
                  <span>
                    <strong>Select participants</strong>
                    <small>Select individuals from the list.</small>
                  </span>
                </label>
              </div>

              <div className={styles.manualRecipientBox}>
                <div>
                  <strong>Add recipient</strong>
                  <p>
                    Add a recipient that is not in the registered participant
                    list. Enter a phone number or an email address.
                  </p>
                </div>
                <div className={styles.manualRecipientForm}>
                  <input
                    className={styles.textInput}
                    value={manualRecipientName}
                    onChange={(event) =>
                      setManualRecipientName(event.target.value)
                    }
                    placeholder="Name (optional)"
                  />
                  <input
                    className={styles.textInput}
                    value={manualRecipientPhone}
                    onChange={(event) =>
                      setManualRecipientPhone(event.target.value)
                    }
                    placeholder="+47 91 70 41 03 or name@example.com"
                  />
                  <Button
                    variant="outlined"
                    type="button"
                    onClick={handleAddManualRecipient}
                  >
                    Add
                  </Button>
                </div>
                {manualRecipientError && (
                  <small className={styles.fieldError}>
                    {manualRecipientError}
                  </small>
                )}
              </div>

              <div
                className={`${styles.recipientPicker} ${
                  recipientControlsDisabled
                    ? styles.recipientPickerDisabled
                    : ''
                }`}
              >
                <div className={styles.recipientTools}>
                  <input
                    className={styles.searchInput}
                    value={recipientSearch}
                    onChange={(event) => setRecipientSearch(event.target.value)}
                    placeholder="Search by name, phone, or email"
                    disabled={recipientControlsDisabled}
                  />
                  <div className={styles.bulkActions}>
                    <button
                      type="button"
                      onClick={handleSelectVisibleRecipients}
                      disabled={recipientControlsDisabled}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={handleClearSelectedRecipients}
                      disabled={recipientControlsDisabled}
                    >
                      Select none
                    </button>
                  </div>
                </div>

                <div className={styles.recipientPickerList}>
                  {!isLoadingRecipients &&
                    !recipientsError &&
                    filteredRecipients.map((recipient) => {
                      const checked =
                        audienceMode === 'all' ||
                        selectedRecipientIds.includes(recipient.id);

                      return (
                        <label
                          className={styles.recipientPickerRow}
                          key={recipient.id}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={recipientControlsDisabled}
                            onChange={() => handleToggleRecipient(recipient.id)}
                          />
                          <span>
                            <strong>{recipient.name}</strong>
                            <small>{getRecipientContact(recipient)}</small>
                          </span>
                        </label>
                      );
                    })}
                  {isLoadingRecipients && (
                    <p className={styles.noRecipients}>
                      Loading registered participants...
                    </p>
                  )}
                  {!isLoadingRecipients && recipientsError && (
                    <p className={styles.noRecipients}>{recipientsError}</p>
                  )}
                  {!isLoadingRecipients &&
                    !recipientsError &&
                    registeredRecipients.length === 0 && (
                      <p className={styles.noRecipients}>
                        No registered participants found for this circle.
                      </p>
                    )}
                  {!isLoadingRecipients &&
                    !recipientsError &&
                    allSelectableRecipients.length > 0 &&
                    filteredRecipients.length === 0 && (
                      <p className={styles.noRecipients}>No matches.</p>
                    )}
                </div>
              </div>

              <p className={styles.selectedCount}>
                {audienceMode === 'all'
                  ? 'All OTH registrations selected'
                  : `${selectedChannelRecipients.length} recipients selected`}
              </p>
            </div>
          )}

          {createStep === 3 && (
            <div className={styles.stepContent}>
              <h3 className={styles.sectionTitle}>Send or schedule</h3>
              <div className={styles.channelCards}>
                <label
                  className={`${styles.optionCard} ${
                    sendTiming === 'now' ? styles.optionCardSelected : ''
                  }`}
                >
                  <input
                    type="radio"
                    checked={sendTiming === 'now'}
                    onChange={() => setSendTiming('now')}
                  />
                  <span>
                    <strong>Send now</strong>
                    <small>
                      The message will be sent as soon as it is ready.
                    </small>
                  </span>
                </label>
                <label
                  className={`${styles.optionCard} ${
                    sendTiming === 'later' ? styles.optionCardSelected : ''
                  }`}
                >
                  <input
                    type="radio"
                    checked={sendTiming === 'later'}
                    onChange={() => setSendTiming('later')}
                  />
                  <span>
                    <strong>Send later</strong>
                    <small>Schedule date and time.</small>
                  </span>
                </label>
              </div>

              {sendTiming === 'later' && (
                <div className={styles.scheduleGrid}>
                  <label className={styles.fieldGroup}>
                    <span>Date</span>
                    <input
                      className={styles.textInput}
                      type="date"
                      value={scheduleDate}
                      min={minScheduleDate}
                      onChange={(event) =>
                        handleScheduleDateChange(event.target.value)
                      }
                    />
                  </label>
                  <label className={styles.fieldGroup}>
                    <span>Time</span>
                    <input
                      className={styles.textInput}
                      type="time"
                      value={scheduleTime}
                      min={
                        scheduleDate === minScheduleDate
                          ? minScheduleTime
                          : undefined
                      }
                      onChange={(event) =>
                        handleScheduleTimeChange(event.target.value)
                      }
                    />
                  </label>
                  <p className={styles.scheduleHint}>
                    The message can be scheduled for any future time.
                  </p>
                </div>
              )}

              <div className={styles.summaryBox}>
                <strong>Summary</strong>
                <span>
                  Channel:{' '}
                  {getChannelSelectionLabel(
                    enabledChannels.sms,
                    enabledChannels.email
                  )}
                </span>
                <span>
                  Recipients:{' '}
                  {audienceMode === 'all'
                    ? 'All OTH registrations'
                    : selectedChannelRecipients.length}
                </span>
                {enabledChannels.sms && (
                  <span>SMS sender: {sender.trim() || 'Backend default'}</span>
                )}
                {enabledChannels.email && (
                  <span>Reply-To: {normalizeReplyToEmail(replyToEmail)}</span>
                )}
                <span>
                  Send time:{' '}
                  {sendTiming === 'now'
                    ? 'Send now'
                    : formatScheduleDate(scheduleDate, scheduleTime)}
                </span>
              </div>
              {submitError && <p className={styles.error}>{submitError}</p>}
            </div>
          )}

          <div className={styles.modalFooter}>
            <Button
              variant="outlined"
              onClick={() =>
                createStep === 1
                  ? setIsCreateModalOpen(false)
                  : setCreateStep((step) => step - 1)
              }
            >
              {createStep === 1 ? 'Cancel' : 'Back'}
            </Button>
            {createStep < 3 ? (
              <Button
                variant="contained"
                onClick={() => setCreateStep((step) => step + 1)}
                disabled={
                  (createStep === 1 && !canContinueFromMessage) ||
                  (createStep === 2 &&
                    audienceMode !== 'all' &&
                    selectedChannelRecipients.length === 0)
                }
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSaveCreatedMessage}
                disabled={!canCreateMessage || isSubmitting}
              >
                {isSubmitting
                  ? 'Working...'
                  : sendTiming === 'now'
                    ? 'Send'
                    : 'Schedule'}
              </Button>
            )}
          </div>
        </div>
      </MuiModal>

      <MuiModal
        open={isDetailsModalOpen}
        setOpen={handleCloseDetails}
        width={640}
      >
        {detailsMessage && (
          <div className={styles.detailsModal}>
            <div className={styles.modalHeader}>
              <div>
                <h2 className={styles.modalTitle}>
                  {detailsMessage.name || 'Untitled communication'}
                </h2>
                <p className={styles.modalSubtitle}>
                  <span className={styles.messageChannel}>
                    <span className={styles.channelIcons}>
                      {getChannelIcon(detailsMessage)}
                    </span>
                    {getChannelLabel(detailsMessage)}
                  </span>
                </p>
              </div>
            </div>

            <div className={styles.detailsMeta}>
              <span>{getSentTimeLabel(detailsMessage)}</span>
              <span
                className={`${styles.recipientsMarker} ${
                  isRecipientsOverlayOpen ? styles.recipientsMarkerOpen : ''
                }`}
                role="button"
                tabIndex={0}
                onClick={() => setIsRecipientsOverlayOpen((isOpen) => !isOpen)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setIsRecipientsOverlayOpen((isOpen) => !isOpen);
                  }
                }}
              >
                {getRecipientCountLabel(detailsMessage)}
                {getMessageRecipients(detailsMessage).length > 0 && (
                  <span className={styles.recipientsOverlay}>
                    {getMessageRecipients(detailsMessage).map(
                      (recipient, index) => (
                        <span
                          key={`${detailsMessage._id}-details-${index}`}
                          className={styles.recipientRow}
                        >
                          <strong>{getRecipientName(recipient)}</strong>
                          <span>{getRecipientContact(recipient)}</span>
                        </span>
                      )
                    )}
                  </span>
                )}
              </span>
            </div>

            {isLoadingDetails && (
              <p className={styles.empty}>Loading message details...</p>
            )}
            {detailsError && <p className={styles.error}>{detailsError}</p>}

            {detailsMessage.sms?.message && (
              <div className={styles.detailsContent}>
                <h3 className={styles.sectionTitle}>SMS</h3>
                {detailsMessage.sms.sender && (
                  <span className={styles.detailsSender}>
                    Sender: {detailsMessage.sms.sender}
                  </span>
                )}
                <p className={styles.messageBody}>
                  {detailsMessage.sms.message}
                </p>
              </div>
            )}

            {detailsMessage.email && (
              <div className={styles.detailsContent}>
                <h3 className={styles.sectionTitle}>Email</h3>
                <span className={styles.detailsSender}>
                  Reply-To:{' '}
                  {getInitialReplyToEmail(detailsMessage.email.replyToEmail)}
                </span>
                {detailsMessage.email.subject && (
                  <span className={styles.detailsSender}>
                    Subject: {detailsMessage.email.subject}
                  </span>
                )}
                <p className={styles.messageBody}>
                  {detailsMessage.email.body}
                </p>
              </div>
            )}
          </div>
        )}
      </MuiModal>

      <Menu
        anchorEl={actionsAnchorEl}
        open={isActionsMenuOpen}
        onClose={handleActionsClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={handleEditDraft}
          disabled={activeMessage?.status !== 'DRAFT'}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit draft</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleStopMessage}
          disabled={activeMessage?.status !== 'SCHEDULED'}
        >
          <ListItemIcon>
            <StopCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cancel scheduled send</ListItemText>
        </MenuItem>
      </Menu>
    </section>
  );
};
